import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { first } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { AuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ToastService } from '@services/common/toast.service';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    // Public
    public resetPasswordForm!: UntypedFormGroup;
    public resetUsername: string = '';
    public resetCode: string = '';
    public loading = false;

    // Private
    private _unsubscribeAll: Subject<any> = new Subject();

    /**
     * Constructor
     *
     * @param _route
     * @param _router
     * @param _authenticationService
     * @param _formBuilder
     */
    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _authenticationService: AuthenticationService,
        private _formBuilder: UntypedFormBuilder,
        private _translateService: CustomTranslateService,
        private toastService: ToastService,
    ) {
        // redirect to home if already logged in
        if (this._authenticationService.currentUserValue) {
            this._router.navigate(['/']);
        }
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.resetPasswordForm.controls;
    }

    /**
     * On Submit
     */
    onSubmit(): void {
        this.resetPasswordForm.markAsPristine();
        // stop here if form is invalid
        if (this.resetPasswordForm.invalid) {
            return;
        }
        this.loading = true;

        // Reset password
        this._authenticationService
            .resetPassword(this.resetUsername, this.f.code.value.toString(), this.f.newPassword.value)
            .pipe(first())
            .subscribe({
                next: (data) => {
                    this.toastService.showSuccess({ details: 'authentication.resetPassword.message.success' });
                    this._router.navigate(['/auth/login'], { queryParams: { msg: 'Your password has been changed' } });
                    this.loading = false;
                },
                error: (error) => {
                    this.toastService.showError({ details: error.message });
                    this.loading = false;
                },
            });
    }

    isFormInvalid(): boolean {
        return (
            this.resetPasswordForm.pristine === true ||
            (this.resetPasswordForm.pristine === false &&
                (this.f.code.invalid || this.f.newPassword.invalid || this.f.confirmPassword.invalid))
        );
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        //TODO: add password match

        // get return url from route parameters or default to '/'
        this.resetUsername = this._route.snapshot.queryParams['username'] || '';
        if (this.resetUsername === '') {
            this._router.navigate(['/']);
        }
        this.resetCode = this._route.snapshot.queryParams['passwordResetCode'] || '';

        this.resetPasswordForm = this._formBuilder.group(
            {
                email: [{ value: this.resetUsername, disabled: true }],
                code: [
                    this.resetCode,
                    [
                        Validators.required,
                        Validators.minLength(6),
                        Validators.maxLength(6),
                        Validators.pattern(/[0-9]+/),
                    ],
                ],
                newPassword: ['', [Validators.required, CustomValidators.passwordValidator]],
                confirmPassword: ['', [Validators.required]],
            },
            {
                validators: [CustomValidators.mustMatch('newPassword', 'confirmPassword')],
            },
        );
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(1);
        this._unsubscribeAll.complete();
    }
}
